import {
  Box3,
  Mesh,
  type Object3D,
  Sphere,
  Vector3,
  type Material,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  FrontSide,
} from 'three';

export interface MachineMapping {
  /** The transparent chamber mesh (where balls live). */
  chamber: Mesh | null;
  /** Chamber's world-space bounding sphere (used to size the ball pool boundary). */
  chamberSphere: Sphere;
  /** Chamber's world-space top Y (where balls exit upward during reveal). */
  chamberTopY: number;
  /** Chamber's world-space center. */
  chamberCenter: Vector3;
  /** Original ball meshes from the GLB — hidden so we can spawn our own. */
  originalBalls: Mesh[];
  /** Logo / branding meshes to hide (if any). */
  logos: Mesh[];
  /** Whole-content bounding sphere for camera framing. */
  contentSphere: Sphere;
  /** Lowest Y of the content (for floor alignment). */
  groundY: number;
  /**
   * World-space target where the selected ball should rest at the end of a reveal.
   * For the placeholder GLB this is Ball_sellecter's centre (the front tray).
   * `null` if no result holder was found — caller falls back to the selection-zone hover position.
   */
  resultHolder: Vector3 | null;
  /**
   * Ordered world-space waypoints describing the visible "track" the ball should follow
   * during a reveal — extracted from meshes named TubeMesh<N>, Tube<N>, Track<N>, etc.
   * Sorted by the trailing integer. Empty if the model has no such meshes (in which case
   * RevealAnimation falls back to its synthetic arch).
   */
  trackWaypoints: Vector3[];
  /**
   * Reference to the selector / cylinder Object3D (Ball_sellecter parent group). The reveal
   * animation animates this node's local position through the GLB's baked selector keyframes
   * at the start of each draw. Null if no such node was found in the model.
   */
  selectorNode: Object3D | null;
  /** Initial local position of the selector node — restored after the reveal sequence finishes. */
  selectorRestPos: Vector3;
  /** Initial local scale of the selector node — restored after the reveal sequence finishes.
   *  The placeholder GLB compresses Ball_sellecter vertically (scale.y ≈ 0.316), making the
   *  cylinder appear short. The reveal temporarily inflates scale.y back toward 1.0. */
  selectorRestScale: Vector3;
  /** The selector mesh's geometry top Y in local-space (max.y of its bounding box). Used to
   *  compute the parent's peak position so the cylinder's tip reaches the chamber exit cap. */
  selectorMeshLocalTopY: number;
}

/**
 * Classify the meshes in a loaded machine GLB.
 * Heuristics are name- and material-aware but kept generic so a future production model still works.
 */
export function buildMachineMapping(root: Object3D): MachineMapping {
  const allMeshes: Mesh[] = [];
  root.traverse((obj) => {
    if (obj instanceof Mesh) allMeshes.push(obj);
  });

  const originalBalls: Mesh[] = [];
  const logos: Mesh[] = [];
  const chamberCandidates: Mesh[] = [];
  let resultHolderMesh: Mesh | null = null;

  for (const mesh of allMeshes) {
    const meshName = mesh.name.toLowerCase();
    const matName = getMaterialName(mesh).toLowerCase();

    if (matName.startsWith('ball_') || matName.startsWith('ball ') || /^ball\d/.test(meshName) || meshName.includes('instancer3_ball')) {
      originalBalls.push(mesh);
      continue;
    }

    if (meshName.includes('logo') || meshName.includes('brand')) {
      logos.push(mesh);
      continue;
    }

    // Result holder — placeholder uses "Ball_sellecter"; generic heuristics
    // also catch "selector", "tray", "result", "holder".
    if (
      !resultHolderMesh &&
      (meshName.includes('sellecter') ||
        meshName.includes('selector') ||
        meshName.includes('tray') ||
        meshName.includes('result') ||
        meshName.includes('ball_holder'))
    ) {
      resultHolderMesh = mesh;
      continue;
    }

    if (matName === 'glass' || matName.includes('glass') || matName.includes('chamber') || meshName.includes('chamber')) {
      chamberCandidates.push(mesh);
      // continue — chamber is not hidden, keep walking
    }
  }

  // Chamber = the largest glass mesh by bounding-sphere radius.
  let chamber: Mesh | null = null;
  let chamberRadius = -Infinity;
  for (const c of chamberCandidates) {
    const sphere = computeWorldBoundingSphere(c);
    if (sphere.radius > chamberRadius) {
      chamberRadius = sphere.radius;
      chamber = c;
    }
  }

  const chamberSphere = chamber ? computeWorldBoundingSphere(chamber) : new Sphere();
  const chamberCenter = chamberSphere.center.clone();
  const chamberBox = chamber ? new Box3().setFromObject(chamber) : new Box3();
  const chamberTopY = chamber ? chamberBox.max.y : 0;

  // Make sure the chamber renders glass-like (transparent, light passes through balls).
  if (chamber) ensureGlassMaterial(chamber);

  // Hide originals + logos.
  for (const ball of originalBalls) ball.visible = false;
  for (const logo of logos) logo.visible = false;

  // Whole content bounds & ground.
  const contentBox = new Box3().setFromObject(root);
  const contentSphere = contentBox.getBoundingSphere(new Sphere());
  const groundY = contentBox.min.y;

  // Result holder world centre, raised slightly so the ball sits on top of the tray rather than inside it.
  // First try the model's identified holder mesh (Ball_sellecter etc.). Only accept it if it lies clearly
  // outside the chamber sphere — otherwise fall back to a procedural "front tray" position below-and-in-front
  // of the chamber, where the reveal ball lands after arcing over the chamber.
  let resultHolder: Vector3 | null = null;
  if (resultHolderMesh && chamber) {
    const holderBox = new Box3().setFromObject(resultHolderMesh);
    const center = holderBox.getCenter(new Vector3());
    center.y = holderBox.max.y;
    const distFromChamber = center.distanceTo(chamberSphere.center);
    if (distFromChamber > chamberSphere.radius * 1.05) {
      resultHolder = center;
    }
  }
  if (!resultHolder && chamber) {
    // Procedural fallback: at the RIGHT side of the chamber base, where the chrome arm
    // visibly ends in the placeholder GLB. The ball will arc UP through the selector cap,
    // OVER the chamber (mirroring the chrome arm silhouette), DOWN onto this spot.
    resultHolder = new Vector3(
      chamberSphere.center.x + chamberSphere.radius * 0.95,
      groundY + chamberSphere.radius * 0.35,
      chamberSphere.center.z + chamberSphere.radius * 0.2,
    );
  }

  // Discover the chrome arm waypoints. Mesh names like TubeMesh2/3/4/5 in the placeholder are
  // the segments of the visible track — order them by the trailing number and grab world centers.
  const trackWaypoints = extractTrackWaypoints(allMeshes);

  // The selector node — the Object3D whose `.position` track is baked in the GLB animation.
  // In the placeholder the named track is "Ball_sellecter.position", which is the PARENT group
  // of the Ball_sellecter_All_Mat_0 mesh. Walk up to find that ancestor so we animate the right
  // transform (animating the leaf mesh's local position would be off by the parent's offset).
  const selectorNode = resultHolderMesh ? findSelectorAncestor(resultHolderMesh) : null;
  const selectorRestPos = selectorNode ? selectorNode.position.clone() : new Vector3();
  const selectorRestScale = selectorNode ? selectorNode.scale.clone() : new Vector3(1, 1, 1);
  const selectorMeshLocalTopY = selectorNode ? computeMeshLocalTopY(selectorNode) : 0;

  return {
    chamber,
    chamberSphere,
    chamberTopY,
    chamberCenter,
    originalBalls,
    logos,
    contentSphere,
    groundY,
    resultHolder,
    trackWaypoints,
    selectorNode,
    selectorRestPos,
    selectorRestScale,
    selectorMeshLocalTopY,
  };
}

/** Effective "top of the cylinder above the parent's origin", expressed in the parent's
 *  UNSCALED local space (i.e., as if parent.scale = 1). This is the value RevealAnimation
 *  uses to solve for the peak parent.y where the cylinder tip lines up with the chamber top.
 *
 *  Implementation: ask Box3.setFromObject for the world-space bbox of the whole selector
 *  subtree (which composes every child's local transform, including any dequantization
 *  scale/offset baked in by KHR_mesh_quantization), then subtract the parent's world Y and
 *  divide by the parent's own scale.y to get the unscaled offset.
 *
 *  This formula is invariant to mesh compression: it gives the same value whether the GLB
 *  is raw or has KHR_mesh_quantization applied. */
function computeMeshLocalTopY(node: Object3D): number {
  node.updateMatrixWorld(true);
  const worldBbox = new Box3().setFromObject(node);
  if (!isFinite(worldBbox.max.y)) return 0;
  const parentWorldY = node.getWorldPosition(new Vector3()).y;
  const worldOffsetAboveParent = worldBbox.max.y - parentWorldY;
  const sy = node.scale.y || 1;
  return worldOffsetAboveParent / sy;
}

const SELECTOR_NAME_RE = /sellecter|selector|cylinder|funnel/i;

/** Walk up the parent chain to find the ancestor whose name matches the selector pattern.
 *  The GLB's animation track targets that ancestor's `.position`, not the leaf mesh's. */
function findSelectorAncestor(mesh: Mesh): Object3D {
  let node: Object3D | null = mesh;
  while (node) {
    if (SELECTOR_NAME_RE.test(node.name)) {
      // Prefer the *highest* matching ancestor — it's the one whose track is animated.
      let topMatch: Object3D = node;
      let walker: Object3D | null = node.parent;
      while (walker) {
        if (SELECTOR_NAME_RE.test(walker.name)) topMatch = walker;
        walker = walker.parent;
      }
      return topMatch;
    }
    node = node.parent;
  }
  return mesh;
}

function extractTrackWaypoints(allMeshes: Mesh[]): Vector3[] {
  // Anything that looks like a numbered tube segment from a lottery-style chrome arm.
  const patterns = [/^tubemesh(\d+)/i, /^tube(\d+)/i, /^track(\d+)/i, /^arm(\d+)/i];
  type Indexed = { mesh: Mesh; order: number };
  const tubes: Indexed[] = [];
  for (const mesh of allMeshes) {
    for (const re of patterns) {
      const m = mesh.name.match(re);
      if (m) {
        tubes.push({ mesh, order: parseInt(m[1]!, 10) });
        break;
      }
    }
  }
  tubes.sort((a, b) => a.order - b.order);
  return tubes.map(({ mesh }) => new Box3().setFromObject(mesh).getCenter(new Vector3()));
}

function getMaterialName(mesh: Mesh): string {
  const m = mesh.material;
  if (Array.isArray(m)) return m[0]?.name ?? '';
  return m?.name ?? '';
}

function computeWorldBoundingSphere(mesh: Mesh): Sphere {
  const box = new Box3().setFromObject(mesh);
  return box.getBoundingSphere(new Sphere());
}

/**
 * Promote the chamber's material to a clearly-transparent glass look so balls inside read through.
 * The GLB's Glass material is already PBR-with-transmission but some viewers set odd defaults.
 */
function ensureGlassMaterial(chamber: Mesh): void {
  const promote = (mat: Material): Material => {
    // If it's already MeshPhysicalMaterial we just tune it.
    if (mat instanceof MeshPhysicalMaterial) {
      mat.transparent = true;
      mat.opacity = mat.opacity > 0 ? mat.opacity : 1.0;
      mat.transmission = Math.max(mat.transmission, 0.95);
      mat.roughness = Math.min(mat.roughness, 0.06);
      mat.metalness = 0;
      mat.thickness = 0.1;
      mat.ior = 1.45;
      mat.envMapIntensity = 1.0;
      mat.side = FrontSide;
      mat.depthWrite = false;
      mat.needsUpdate = true;
      return mat;
    }
    // Otherwise build a fresh physical material that emulates glass.
    const std = mat instanceof MeshStandardMaterial ? mat : null;
    const phys = new MeshPhysicalMaterial({
      color: std?.color ?? 0xffffff,
      metalness: 0,
      roughness: 0.04,
      transmission: 0.97,
      thickness: 0.1,
      ior: 1.45,
      transparent: true,
      depthWrite: false,
      side: FrontSide,
      envMapIntensity: 1.0,
    });
    phys.name = mat.name || 'GlassPromoted';
    return phys;
  };

  if (Array.isArray(chamber.material)) {
    chamber.material = chamber.material.map(promote);
  } else if (chamber.material) {
    chamber.material = promote(chamber.material);
  }

  chamber.renderOrder = 2; // render after opaque balls
  chamber.castShadow = false;
  chamber.receiveShadow = false;
}
